/* eslint-disable no-restricted-syntax */
import { t } from "i18next";
import lod_ from "lodash";
import { Divider, Icon, InputAdornment } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDInput from "components/Basics/MDInput";
import ButtonMenu from "components/Custom/ButtonMenu";
import DateFilter from "components/Custom/Filters/DateFilter";
import { getLabel } from "helpers/dictionary";
import { useDispatch, useSelector } from "react-redux";
import { deleteFilter, setFilter } from "redux-react/reducers/filtersReducers";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";
import InputString from "./filters/InputString";
import {
	getLocalStorageFrontValues,
	getLocalStorageBackValues
} from "components/Custom/Filters/filters";
import { useCallback, useEffect, useState } from "react";
import ChartsActions from "redux-react/actions/chartsActions";

/**
 * Parse filters defined by string into
 * an component's array
 * @param {[String]} filters
 */
function useFilters(route) {
	const page = route.route;

	const profile = useSelector(selectCurrentProfile);
	const language = profile?.language || "fr";
	const assistantID = profile?.assistantID;
	const dispatch = useDispatch();

	const [loaded, setLoaded] = useState(false);
	const [component, setComponent] = useState([]);
	// Page filters
	const [pageFilters, setPageFilters] = useState({});
	// Filters configuration for the page
	const [buildedPageFilters, setBuildedPageFilters] = useState({});
	// Store filters
	const filters = useSelector(state => state.filters);
	// Key to reset filters
	const [key, setKey] = useState(null);

	/**
	 * Get page filters from backend
	 * @return {Promise} filters - page filters
	 */
	const getPageFilters = useCallback(async () => {
		return new Promise((resolve, reject) => {
			dispatch(
				ChartsActions.getPageFilters(profile.assistantID, route, res => {
					resolve(res.filters);
				})
			);
		});
	}, [dispatch, assistantID, route]);

	/**
	 * Build page filters from local storage and default filters
	 * @param {object} myPageFilters - filters defined on the page
	 * @param {boolean} loadDefault - load default filters or not
	 * @return {object} actualFilters - built filters
	 */
	const buildPageFilters = (myPageFilters, loadDefault = false) => {
		// Get filters from local storage
		let actualFilters = getLocalStorageBackValues(profile.assistantID, route.route, filters);
		// If there is default filters, set them (ONLY ON PAGE LOAD)
		// if (!lod_.isEmpty(defaultFilters) && loadDefault) {
		/* Object.keys(actualFilters).map(key => {
			let filter = actualFilters[key];
			let defaultFilter = defaultFilters[key];
			// Replace value if it is empty
			if (
				lod_.isEmpty(filter?.values) &&
				lod_.isEmpty(filter?.value) &&
				!lod_.isNil(defaultFilter)
			) {
				actualFilters[key] = {
					...defaultFilter
				};
			}
		}); */
		// }

		// Remove unused filters on page, we can have filters in local storage that are not on the page
		Object.keys(actualFilters).map(key => {
			let filter = actualFilters[key];
			let pageFilter = (myPageFilters ?? []).find(
				pageFilter => pageFilter.attribute === filter.name
			);

			if (!pageFilter) {
				delete actualFilters[key];
				// delete filter from local storage
				let filterObject = {
					assistantID: profile.assistantID,
					page: route.route,
					attribute: filter.name
				};
				dispatch(deleteFilter(filterObject));
			}
		});

		return actualFilters;
	};

	/**
	 * Load page filters
	 */
	const loadFilters = useCallback(async () => {
		// Set page filters
		let myPageFilters = await getPageFilters();
		setPageFilters(myPageFilters);
		let actualFilters = buildPageFilters(myPageFilters, true);
		setBuildedPageFilters(actualFilters);

		return myPageFilters;
	}, [profile.selectedAssistant.assistantID, route.route]);

	/**
	 * Set filter to store
	 * @param {string} assistantID - assistant identifier
	 * @param {string} page - page identifier
	 * @param {object} filter - filter object
	 * @param {any} front - front value
	 * @param {any} back - back value
	 */
	function setFilterToStore(assistantID, page, filter, front, back) {
		dispatch(
			setFilter({
				assistantID,
				page,
				attribute: filter.attribute,
				front,
				back: {
					name: filter.attribute,
					value: back,
					type: filter.type,
					dictionaryPath: filter.dictionaryPath,
					links: filter.links
				}
			})
		);
	}

	/**
	 * Update store filter
	 * @param {any} value - value to set
	 * @param {object} filter - filter object
	 * @param {string} page - page identifier
	 */
	const updateStoreFilter = (value, filter, page) => {
		if (lod_.isNil(value) || lod_.isEmpty(value)) {
			dispatch(
				deleteFilter({
					assistantID,
					page,
					attribute: filter.attribute
				})
			);
		} else {
			const mongoRegex = { $in: value };
			// const mongoRegex = { $regex: value, $options: "i" };
			setFilterToStore(assistantID, page, filter, value, mongoRegex);
		}
	};

	/**
	 * Debounced version of updateStoreFilter
	 */
	const updateStoreFilterDebounce = lod_.debounce((value, filter, page) => {
		if (!value.trim()) {
			dispatch(
				deleteFilter({
					assistantID,
					page,
					attribute: filter.attribute
				})
			);
		} else {
			const mongoRegex = { $regex: value, $options: "i" };
			setFilterToStore(assistantID, page, filter, value, mongoRegex);
		}
	}, 300);

	/**
	 * Create component filters
	 */
	const createComponentFilters = async () => {
		const loadedPageFilters = await loadFilters();

		let newComponentFilters = [];

		for (let filter of loadedPageFilters) {
			switch (filter.type) {
				case "boolean":
					newComponentFilters.push(
						<MDBox key={filter.attribute} mr={1}>
							<ButtonMenu
								fullHeight
								title={getLabel(filter.label, language)}
								type="boolean"
								inverted={filter.inverted}
								optionnal={filter.optionnal}
								storage={getLocalStorageFrontValues(assistantID, page, filter.attribute, filters)}
								handleUpdate={items => {
									if (lod_.isEmpty(items)) {
										let filterObject = {
											assistantID,
											page,
											attribute: filter.attribute
										};

										dispatch(deleteFilter(filterObject));
									} else {
										let value = lod_.isObject(items.value) ? items.value : { $in: [items.value] };
										setFilterToStore(assistantID, page, filter, items, value);
									}
								}}
							/>
						</MDBox>
					);
					break;
				case "datetime":
				case "date":
					newComponentFilters.push(
						<MDBox mr={1} key={filter.attribute} display="flex">
							<DateFilter
								assistantID={assistantID}
								page={page}
								storage={getLocalStorageFrontValues(assistantID, page, filter.attribute, filters)}
								tooltip
								attribute={filter.attribute}
								title={
									getLabel(filter.label, language) ||
									filter.label ||
									t(`FILTERS.${filter.attribute}`)
								}
							/>
						</MDBox>
					);
					break;
				case "code":
					newComponentFilters.push(
						<MDBox key={filter.dictionaryPath} mr={1}>
							<ButtonMenu
								fullHeight
								title={getLabel(filter.label, language)}
								items={filter.values}
								storage={getLocalStorageFrontValues(assistantID, page, filter.attribute, filters)}
								handleUpdate={items => {
									if (!items.length) {
										let filterObject = {
											assistantID,
											page,
											attribute: filter.attribute
										};

										dispatch(deleteFilter(filterObject));
									} else {
										let values = items.map(i => i.value);
										setFilterToStore(assistantID, page, filter, items, { $in: values });
									}
								}}
							/>
						</MDBox>
					);
					break;
				default:
					if (filter.type === "string") {
						newComponentFilters.push(
							<InputString
								storage={getLocalStorageFrontValues(assistantID, page, filter.attribute, filters)}
								filter={filter}
								page={page}
								language={language}
								updateStoreFilterDebounce={updateStoreFilterDebounce}
								updateStoreFilter={updateStoreFilter}
							/>
						);
					} else {
						newComponentFilters.push(
							<MDBox key={filter.attribute} mr={1}>
								<MDInput
									label={filter.label.fr}
									type={filter.type}
									value={getLocalStorageFrontValues(assistantID, page, filter.attribute, filters)}
									storage={getLocalStorageFrontValues(assistantID, page, filter.attribute, filters)}
									onChange={e => {
										if (lod_.isNil(e.target.value) || lod_.isEmpty(e.target.value)) {
											dispatch(
												deleteFilter({
													assistantID,
													page,
													attribute: filter.attribute
												})
											);
										} else {
											setFilterToStore(assistantID, page, filter, e.target.value, e.target.value);
										}
									}}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start" sx={{ ml: 1, mr: 0 }}>
												<Icon size="small">filter_alt</Icon>
											</InputAdornment>
										)
									}}
									placeholder={`Filtrer ${getLabel(filter.label, language)} ...`}
								/>
							</MDBox>
						);
					}
					break;
			}
		}

		if (!lod_.isEmpty(newComponentFilters)) {
			newComponentFilters.unshift(
				<Divider
					variant="vertical"
					sx={{
						marginLeft: 1,
						width: "2px",
						minHeight: "5vh",
						height: "100%",
						color: "#ccc",
						opacity: "0.6",
						backgroundImage:
							"linear-gradient(to right, rgba(52, 71, 103, 0), rgba(52, 71, 103, 0.4), rgba(52, 71, 103, 0)) !important"
					}}
				/>
			);
		}

		setComponent(newComponentFilters);
		setLoaded(true);
		setKey(page);
	};

	/**
	 * Reset filters state
	 */
	const reset = () => {
		setKey(null);
		setComponent([]);
		setLoaded(false);
		setBuildedPageFilters({});
		setPageFilters({});
	};

	/**
	 * Mount filters
	 */
	const mount = async () => {
		await createComponentFilters();
	};

	/**
	 * Reset immediately when route or assistantID changes
	 */
	useEffect(() => {
		// Immediate cleanup of old values
		reset();

		// Then mount new filters
		mount();

		// Cleanup on unmount
		return () => {
			reset();
		};
	}, [route.route, assistantID]);

	/**
	 * Update page filters when a filter is changed
	 */
	useEffect(() => {
		if (loaded !== true) {
			return;
		}

		if (!lod_.isEmpty(pageFilters)) {
			const actualFilters = buildPageFilters(pageFilters);
			setBuildedPageFilters(actualFilters);
		} else {
			setBuildedPageFilters({});
		}
	}, [filters, pageFilters]);

	return {
		route: key,
		component,
		loaded: loaded && key === page,
		pageFilters: buildedPageFilters,
		//
		buildPageFilters
	};
}

export default useFilters;
